import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function EmailTemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [template, setTemplate] = useState({
    name: '',
    area: 'quote',
    subject: '',
    body: '',
    is_default: false
  });

  const [saving, setSaving] = useState(false);
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchTemplate();
    }
  }, [id]);

  const fetchTemplate = async () => {
    try {
      const response = await apiFetch(`/api/email-templates/${id}`);
      const data = await response.json();
      setTemplate(data);
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const handleSave = async () => {
    if (!template.name || !template.subject || !template.body) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const url = isNew
        ? '/api/email-templates'
        : `/api/email-templates/${id}`;

      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template)
      });

      if (!response.ok) throw new Error('Failed to save');

      const data = await response.json();

      if (isNew) {
        navigate(`/email-templates/${data.id}`);
      }

      alert('Email template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save email template');
    } finally {
      setSaving(false);
    }
  };

  const variables = {
    quote: [
      '{{customer_name}}',
      '{{quote_number}}',
      '{{quote_date}}',
      '{{quote_total}}',
      '{{valid_until}}',
      '{{company_name}}',
      '{{company_phone}}',
      '{{company_email}}'
    ],
    invoice: [
      '{{customer_name}}',
      '{{invoice_number}}',
      '{{invoice_date}}',
      '{{invoice_total}}',
      '{{payment_terms}}',
      '{{company_name}}',
      '{{company_phone}}',
      '{{company_email}}'
    ],
    job: [
      '{{customer_name}}',
      '{{job_number}}',
      '{{completion_date}}',
      '{{engineer_name}}',
      '{{site_address}}',
      '{{company_name}}',
      '{{company_phone}}',
      '{{company_email}}'
    ],
    sales_order: [
      '{{customer_name}}',
      '{{order_number}}',
      '{{order_date}}',
      '{{delivery_address}}',
      '{{order_total}}',
      '{{company_name}}',
      '{{company_phone}}',
      '{{company_email}}'
    ]
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = template.body;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    setTemplate({
      ...template,
      body: before + variable + after
    });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      textarea.focus();
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/email-templates')}
              className="text-white hover:text-indigo-100 transition text-sm"
            >
              ← Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-2xl font-light">
                {isNew ? 'New Email Template' : 'Edit Email Template'}
              </h1>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg font-medium transition border border-white/20 text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : '💾 Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-6">

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">

          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={template.name}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Professional Quote Email"
            />
          </div>

          {/* Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={template.area}
              onChange={(e) => setTemplate({ ...template, area: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="quote">Quotes</option>
              <option value="invoice">Invoices</option>
              <option value="job">Jobs</option>
              <option value="sales_order">Sales Orders</option>
            </select>
          </div>

          {/* Default */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_default"
              checked={template.is_default}
              onChange={(e) => setTemplate({ ...template, is_default: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
              Set as default template for this document type
            </label>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={template.subject}
              onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Quote {{quote_number}} from {{company_name}}"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use {'{{'}variable_name{'}}'} for dynamic content
            </p>
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Email Body (HTML) <span className="text-red-500">*</span>
              </label>
              <button
                onClick={() => setShowVariables(!showVariables)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                {showVariables ? 'Hide' : 'Show'} Variables
              </button>
            </div>

            {showVariables && (
              <div className="mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-2">Available Variables:</div>
                <div className="flex flex-wrap gap-2">
                  {variables[template.area].map(variable => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-indigo-50 hover:border-indigo-300 transition"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <textarea
              id="body"
              value={template.body}
              onChange={(e) => setTemplate({ ...template, body: e.target.value })}
              rows={20}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              placeholder="Enter email body with HTML formatting..."
            />
            <p className="text-xs text-gray-500 mt-1">
              You can use HTML tags for formatting: &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;br&gt;, etc.
            </p>
          </div>

          {/* Preview Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="text-sm text-blue-800">
              <strong>💡 Tip:</strong> When sending an email, you'll be able to preview how it looks with actual data before sending.
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default EmailTemplateDetail;
